import React, { useState, useContext, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import { useNavigate } from 'react-router';
import SignupMultistep from '../user/SignupMultistep';
import AuthRedirect from '../components/AuthRedirect';
import { GuestSlide } from './components/GuestSlide';
import ActivityFeed from './components/ActivityFeed';
import OfferCountdown from './components/OfferCountdown';
import TrustBadges from './components/TrustBadges';
import ExitIntentPopup from './components/ExitIntentPopup';
import { AppContext } from '../window';
import { NotificationContext } from '../context/NotificationContext';
import { HeartFill } from 'react-bootstrap-icons';
import { WrapperContext } from '../context/WrapperContext';
import { useTranslation } from '../i18n';
import { useTracking } from '../hooks/useTracking';

export const LandingPageC: React.FC = () => {
    const { location } = useContext(AppContext);
    const { addNotification } = useContext(NotificationContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const track = useTracking();

    // Fire advertiser_page on mount — media-buy landing page view
    useEffect(() => {
        track('advertiser_page');
    }, []);

    // Fire page_interact on first click/tap anywhere on the page
    useEffect(() => {
        const handler = () => { track('page_interact'); };
        document.addEventListener('click', handler, { once: true, capture: true });
        return () => { document.removeEventListener('click', handler, { capture: true }); };
    }, []);

    const [selectedGenderId, setSelectedGenderId] = useState<number>(0);
    const [selectedAgegroupId, setSelectedAgegroupId] = useState<number>(0);
    const [showSignup, setShowSignup] = useState(false);
    const [profilesViewed, setProfilesViewed] = useState(0);
    const [sliderProfileCount, setSliderProfileCount] = useState(0);
    const [likedProfile, setLikedProfile] = useState<string | null>(null);
    const [likedUserId, setLikedUserId] = useState<number | null>(null);
        const { setWrapperClasses } = useContext(WrapperContext);
    

    useEffect(() => {
    setWrapperClasses('d-flex');
        
        return () => {
            setWrapperClasses('');
        };
    }, [setWrapperClasses]);


    // Handle hash navigation for back button
    useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash === '#signup') {
                setShowSignup(true);
            } else {
                setShowSignup(false);
            }
        };
        
        // Check initial hash
        handleHashChange();
        
        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);
    
    // Calculate dynamic pricing based on selections
    const getDiscountPercentage = () => {
        const base = 10;
        const selectionBonus = (selectedGenderId ? 5 : 0) + (selectedAgegroupId ? 5 : 0);
        const profileBonus = Math.min(profilesViewed * 2, 10);
        return Math.min(base + selectionBonus + profileBonus, 30);
    };

    const handleSelectionComplete = (genderId: number, agegroupId: number) => {
        setSelectedGenderId(genderId);
        setSelectedAgegroupId(agegroupId);
    };

    const handleUnlockPrompt = (fromHeart: boolean = false, profileName?: string, userId?: number) => {
        track('cta_click');
        if (fromHeart && profileName) {
            setLikedProfile(profileName);
            if (userId) {
                setLikedUserId(userId);
            }
            addNotification({
                message: t('landing_c.you_liked_profile', { profileName }) + ' ' + t('landing_c.liked_signup_message'),
                level: 'success'
            });
        } else if (sliderProfileCount >= 2) {
            addNotification({
                message: t('landing_c.profile_limit_reached'),
                level: 'info'
            });
        }
        
        // Use hash navigation to enable back button
        window.location.hash = 'signup';
    };

    const handleProfileView = () => {
        const newCount = sliderProfileCount + 1;
        setSliderProfileCount(newCount);
        
        // Auto-show signup after 3 profiles
        if (newCount >= 3) {
            addNotification({
                message: t('landing_c.viewed_profiles_signup', { count: 3 }),
                level: 'info'
            });
            window.location.hash = 'signup';
        }
    };

    // Track profile views
    useEffect(() => {
        const interval = setInterval(() => {
            if (selectedGenderId && selectedAgegroupId && !showSignup) {
                setProfilesViewed(prev => prev + 1);
            }
        }, 8000);

        return () => clearInterval(interval);
    }, [selectedGenderId, selectedAgegroupId, showSignup]);

    const inferAgeRange = () => {
        // Map agegroup ID to signup age range format
        // Note: This mapping may need adjustment based on actual attribute IDs
        // For now, returning a default until we have access to attribute values
        return '18-24';
    };

    const signupInitialData = {
        initialGender: 'male',
        initialAgeRange: inferAgeRange(),
        initialTags: [], // No tags in the new system
        initialLikesAttributes: [selectedGenderId, selectedAgegroupId].filter(id => id > 0)
    };

    return (
        <div className="landing-page-c">
            <AuthRedirect />
            <ExitIntentPopup onStay={() => {
                window.location.hash = 'signup';
            }} />
            <ActivityFeed />
            


            {/* Main content */}
            <Container className="py-3 pb-5">
                <div className={`transition-wrapper ${showSignup ? 'show-signup' : ''}`}>
                {!showSignup ? (
                    <>
                        {/* Profile slider with benefits side by side on desktop */}
                        <Row className="g-4 align-items-center">
                            {/* Profile slider */}
                            <Col lg={6}>
                                <GuestSlide
                                    onUnlockPrompt={handleUnlockPrompt}
                                    onSelectionComplete={handleSelectionComplete}
                                    selectedGenderId={selectedGenderId}
                                    selectedAgegroupId={selectedAgegroupId}
                                    onProfileView={handleProfileView}
                                    profileViewCount={sliderProfileCount}
                                />
                            </Col>
                            <Col lg={6}>
                                <div className="pb-2">
                                    <Container>
                                        <OfferCountdown />
                                    </Container>
                                </div>
                                <div className="text-center mb-4">
                                    <h1 className="display-5 fw-bold text-uppercase mb-3">
                                        <span className="gradient-text">
                                            {t('landing_c.exclusive_singles')}
                                        </span>
                                        <br />
                                        <span>
                                            {location?.town?.name 
                                                ? t('landing_c.in_location', { location: location.town.name })
                                                : t('landing_c.in_your_area')}
                                        </span>
                                    </h1>
                                    <p className="text-muted">
                                        {t('landing_c.select_interests')}
                                    </p>
                                </div>
                                {/* Benefits box */}
                                <div className="benefits-box p-4 rounded">
                                    <h3 className="h4 mb-3 text-center">
                                        <span className="gradient-text">{t('landing_c.why_join_now')}</span>
                                    </h3>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-2 text-muted">
                                            <i className="text-success me-2">✓</i>
                                            {t('landing_c.instant_matches')}
                                        </li>
                                        <li className="mb-2 text-muted">
                                            <i className="text-success me-2">✓</i>
                                            {location?.town?.name 
                                                ? t('landing_c.members_online_location', { location: location.town.name })
                                                : t('landing_c.members_online_local')}
                                        </li>
                                        <li className="mb-2 text-warning">
                                            <i className="text-warning me-2">✓</i>
                                            <strong>{t('landing_c.discount_expires', { percentage: getDiscountPercentage() })}</strong>
                                        </li>
                                        <li className="text-muted">
                                            <i className="text-success me-2">✓</i>
                                            {t('landing_c.private_discreet')}
                                        </li>
                                    </ul>

                                    {/* CTA button for mobile */}
                                    <div className="d-lg-none mt-4">
                                        <Button 
                                            variant="primary" 
                                            size="lg" 
                                            className="w-100"
                                            onClick={() => handleUnlockPrompt()}
                                        >
                                            {t('landing_c.unlock_all_profiles')}
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </>
                ) : (
                    /* Signup form */
                    <Row>
                        <Col lg={6} className="mx-auto">
                            <div className="bg-body rounded p-4 animate-in">
                                {likedProfile && (
                                    <div className="text-center mb-4">
                                        <div className="badge bg-success px-3 py-2 mb-3">
                                            <HeartFill className="me-1" />
                                            {t('landing_c.you_liked_profile', { profileName: likedProfile })}
                                        </div>
                                        <p className="text-muted small">
                                            {t('landing_c.create_profile_to_match')}
                                        </p>
                                    </div>
                                )}
                                <h3 className="text-center mb-4">{t('landing_c.complete_your_profile')}</h3>
                                <SignupMultistep
                                    onSignupSuccess={() => {
                                        if (likedUserId) {
                                            navigate(`/inbox/${likedUserId}`);
                                        } else {
                                            navigate('/match');
                                        }
                                    }}
                                    submitButtonText={t('landing_c.start_matching')}
                                    {...signupInitialData}
                                />
                                <TrustBadges className="mt-3" />
                            </div>
                        </Col>
                    </Row>
                )}
                </div>
            </Container>
        </div>
    );
};